import type { ReactNode } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

const TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      bgcolor: "grey.700",
      borderRadius: 1,
      px: 1,
      py: 0.5
    }
  },
  arrow: {
    sx: {
      color: "grey.700"
    }
  }
} as const;

const triggerSpanSx = {
  display: "inline-flex",
  alignItems: "center",
  lineHeight: 0,
  verticalAlign: "middle" as const,
  /** Breathing room from adjacent controls (inline axis). */
  px: 1
};

export interface InlineHelpTooltipIconProps {
  readonly title: ReactNode;
  readonly ariaLabel: string;
  readonly disabled?: boolean;
  readonly color?: string;
}

/**
 * Non-button help glyph: hover shows `title` (same tooltip chrome as model help in Frame/Video panels).
 */
export function InlineHelpTooltipIcon({ title, ariaLabel, disabled = false, color = "text.secondary" }: InlineHelpTooltipIconProps) {
  if (disabled) {
    return (
      <Box component="span" sx={{ ...triggerSpanSx, color, opacity: 0.38, pointerEvents: "none" }} aria-hidden>
        <HelpOutlineOutlinedIcon fontSize="small" />
      </Box>
    );
  }

  return (
    <Tooltip title={title} placement="bottom" arrow slotProps={TOOLTIP_SLOT_PROPS} disableFocusListener enterDelay={0} enterNextDelay={0}>
      <Box
        component="span"
        sx={{ ...triggerSpanSx, color, cursor: "help" }}
        aria-label={ariaLabel}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
      >
        <HelpOutlineOutlinedIcon fontSize="small" />
      </Box>
    </Tooltip>
  );
}

export interface GenerationModelHelpIconProps {
  /** Display name of the active model (from metadata). */
  readonly modelLabel: string;
  /** Max reference images for the active model (same as ReferenceImagesAndPrompt `maxReferenceImages`). */
  readonly maxReferenceImages: number;
  readonly disabled?: boolean;
}

/**
 * Help glyph for image/video generation model dropdowns: hover shows max reference images (not a button).
 */
export function GenerationModelHelpIcon({ modelLabel, maxReferenceImages, disabled = false }: GenerationModelHelpIconProps) {
  const n = maxReferenceImages;
  const label = modelLabel.trim() === "" ? "This model" : modelLabel;
  const refWord = n === 1 ? "reference image" : "reference images";

  const title = (
    <Typography sx={{ fontSize: 10, fontWeight: 500, lineHeight: "16px", maxWidth: 280 }}>
      <Box component="span" sx={{ fontWeight: 700 }}>
        {label}
      </Box>
      {" — up to "}
      <Box component="span" sx={{ fontWeight: 700 }}>
        {n}
      </Box>
      {` ${refWord} per run.`}
    </Typography>
  );

  return <InlineHelpTooltipIcon title={title} ariaLabel="Model and reference limit help" disabled={disabled} />;
}
